import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../common/Pagination';
import BulkActions from '../common/BulkActions';
import useBulkSelection from '../../hooks/useBulkSelection';
import * as XLSX from 'xlsx';
import './UserManagement.css';
import { formatAuditActorLabel } from '../../utils/auditActorDisplay';

const UserManagement = () => {
  const { t } = useTranslation('navigation');
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const searchDebounceRef = useRef(null);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Helper functions to translate role and status
  const translateRole = (role) => {
    if (!role) return '';
    const roleMap = {
      'student': t('userManagement.forms.student'),
      'teacher': t('userManagement.forms.teacher'),
      'admin': t('userManagement.forms.admin'),
      'platform_admin': t('userManagement.forms.admin'),
      'school_admin': t('userManagement.forms.admin'),
      'parent': t('userManagement.forms.parent'),
      'agent': t('userManagement.forms.agent')
    };
    return roleMap[role.toLowerCase()] || role;
  };
  
  const translateStatus = (status) => {
    if (!status) return '';
    return status === 'active' ? t('userManagement.forms.active') : t('userManagement.forms.inactive');
  };
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('user-list');
  const [error, setError] = useState(null);
  const [showMassEmailModal, setShowMassEmailModal] = useState(false);
  const [massEmailSubject, setMassEmailSubject] = useState('');
  const [massEmailMessage, setMassEmailMessage] = useState('');
  const [sendingMassEmail, setSendingMassEmail] = useState(false);
  const [lastBulkEmailResult, setLastBulkEmailResult] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [loadingSecurity, setLoadingSecurity] = useState(false);
  const [communicationLogs, setCommunicationLogs] = useState([]);
  const [loadingCommunication, setLoadingCommunication] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    role: 'student',
    status: 'active'
  });

  // API Base URL - Use environment variable or fallback
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  // Get authentication token
  const getAuthToken = () => {
    return (
      sessionStorage.getItem('adminToken') ||
      sessionStorage.getItem('token') ||
      localStorage.getItem('adminToken') ||
      localStorage.getItem('token')
    );
  };

  // Get auth headers
  const getAuthHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const formatTimeAgo = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  };

  const fetchActivityLogs = useCallback(async (search = '') => {
    try {
      setLoadingActivity(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '50');
      if (search && search.trim()) params.set('search', search.trim());

      const response = await fetch(`${API_BASE_URL}/api/audit-logs?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data?.message || data?.error || 'Failed to fetch activity logs');
      }

      const logs = Array.isArray(data.logs) ? data.logs : [];
      setActivityLogs(logs);
    } catch (e) {
      console.error('Error fetching activity logs:', e);
      setActivityLogs([]);
      setError(e.message);
    } finally {
      setLoadingActivity(false);
    }
  }, [API_BASE_URL]);

  const guessSecurityEvent = (log) => {
    const action = (log?.action || '').toLowerCase();
    const desc = (log?.description || '').toLowerCase();
    if (action.includes('login') && (action.includes('fail') || desc.includes('fail'))) return 'Failed Login';
    if (action.includes('password') || desc.includes('password')) return 'Password / Security Change';
    if (action.includes('auth') || desc.includes('suspicious') || desc.includes('unauthorized')) return 'Suspicious Activity';
    return (log?.action || 'Security Event');
  };

  const guessSeverity = (log) => {
    const status = (log?.status || '').toLowerCase();
    const action = (log?.action || '').toLowerCase();
    const desc = (log?.description || '').toLowerCase();
    if (status && status !== 'success') return 'High';
    if (action.includes('delete') || desc.includes('suspicious') || desc.includes('unauthorized')) return 'High';
    if (action.includes('login')) return 'Medium';
    return 'Low';
  };

  const fetchSecurityLogs = useCallback(async (search = '') => {
    try {
      setLoadingSecurity(true);
      setError(null);

      // Leverage audit logs; optionally narrow via search keyword
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '50');
      // If user typed a search, use it; otherwise bias toward auth/security keywords
      const q = search && search.trim() ? search.trim() : 'login';
      params.set('search', q);

      const response = await fetch(`${API_BASE_URL}/api/audit-logs?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data?.message || data?.error || 'Failed to fetch security events');
      }

      const logs = Array.isArray(data.logs) ? data.logs : [];
      setSecurityLogs(logs);
    } catch (e) {
      console.error('Error fetching security logs:', e);
      setSecurityLogs([]);
      setError(e.message);
    } finally {
      setLoadingSecurity(false);
    }
  }, [API_BASE_URL]);

  const fetchCommunicationLogs = useCallback(async (search = '') => {
    try {
      setLoadingCommunication(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('limit', '50');
      // Default to bulk email history unless user searches something else
      const q = search && search.trim() ? search.trim() : 'bulk.email';
      params.set('search', q);

      const response = await fetch(`${API_BASE_URL}/api/audit-logs?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data?.message || data?.error || 'Failed to fetch communication logs');
      }

      const logs = Array.isArray(data.logs) ? data.logs : [];
      setCommunicationLogs(logs);
    } catch (e) {
      console.error('Error fetching communication logs:', e);
      setCommunicationLogs([]);
      setError(e.message);
    } finally {
      setLoadingCommunication(false);
    }
  }, [API_BASE_URL]);

  const sendMassEmail = async () => {
    try {
      const subject = massEmailSubject.trim();
      const message = massEmailMessage.trim();
      if (!subject || !message) {
        alert('Subject and message are required.');
        return;
      }

      setSendingMassEmail(true);
      setError(null);

      const payload = {
        subject,
        message,
        search: debouncedSearchTerm || ''
      };

      const response = await fetch(`${API_BASE_URL}/api/admin/bulk-email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const msg = data?.message || data?.error || 'Failed to send bulk email';
        throw new Error(msg);
      }

      setLastBulkEmailResult({
        id: Date.now(),
        operation: 'Mass Email',
        target: `${data.recipients || 0} users`,
        type: 'Email',
        status: data.failed > 0 ? 'Partial' : 'Completed',
        created: new Date().toISOString().slice(0, 10),
        sent: data.sent,
        failed: data.failed
      });

      setShowMassEmailModal(false);
      setMassEmailSubject('');
      setMassEmailMessage('');
      alert(`Sent: ${data.sent} / ${data.recipients} (Failed: ${data.failed})`);
    } catch (e) {
      console.error(e);
      setError(e.message);
      alert(`Bulk email failed: ${e.message}`);
    } finally {
      setSendingMassEmail(false);
    }
  };

  // Fetch users from backend API with server-side pagination and search
  const fetchUsers = useCallback(async (page = 1, limit = 25, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search && search.trim()) params.set('search', search.trim());

      const response = await fetch(`${API_BASE_URL}/api/admin/users?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 403 || response.status === 401) {
          // Token expired or invalid
          sessionStorage.removeItem('adminToken');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('adminUser');
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('refreshToken');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('token');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('user');
          localStorage.removeItem('refreshToken');
          window.location.href = '/';
          return;
        }
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();

      // Support both paginated response (users + total) and legacy (users only)
      const total = data.total ?? data.pagination?.total ?? (data.users ? data.users.length : 0);
      const userList = data.users || [];

      // No users found (real empty state)
      if (userList.length === 0 && total === 0) {
        setUsers([]);
        setTotalCount(0);
        setError(null);
        return;
      }

      const transformedUsers = userList.map(user => ({
        id: user.id,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        email: user.email,
        role: user.role || 'student',
        status: user.is_active ? 'active' : 'inactive',
        joinDate: user.created_at ? new Date(user.created_at).toISOString().split('T')[0] : '',
        country: user.country || 'N/A',
        phone: user.phone || 'N/A',
        first_name: user.first_name,
        last_name: user.last_name
      }));

      setUsers(transformedUsers);
      setTotalCount(total);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users from server');
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  // Create new user via API
  const createUser = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Show detailed validation errors if available
        if (errorData.details && Array.isArray(errorData.details)) {
          const errorMessages = errorData.details.map(d => `${d.field}: ${d.message}`).join('\n');
          throw new Error(`Validation failed:\n${errorMessages}`);
        }
        throw new Error(errorData.error || errorData.message || 'Failed to create user');
      }

      const data = await response.json();
      console.log('User created:', data);
      
      // Refresh user list
      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        role: 'student',
        status: 'active'
      });
      setShowAddForm(false);
      
      alert('User created successfully!');
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message);
      alert(`Failed to create user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update user via API
  const updateUser = async (userId, userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      console.log('User updated successfully');
      
      // Refresh user list
      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      
      // Reset editing state
      setEditingUser(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        role: 'student',
        status: 'active'
      });
      
      alert('User updated successfully!');
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.message);
      alert(`Failed to update user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete user via API
  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      console.log('User deleted successfully');
      
      // Refresh user list
      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      
      alert('User deleted successfully!');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message);
      alert(`Failed to delete user: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Toggle user status via API
  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      setLoading(true);
      setError(null);
      
      const newStatus = currentStatus === 'active' ? false : true;
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user status');
      }

      console.log('User status updated successfully');
      
      // Refresh user list
      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      
      alert(`User ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.message);
      alert(`Failed to update user status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const prevSearchRef = useRef(debouncedSearchTerm);

  // Debounce search term for server-side search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm]);

  // Load users when page, page size, or search changes (server-side pagination)
  useEffect(() => {
    const searchChanged = prevSearchRef.current !== debouncedSearchTerm;
    if (searchChanged) {
      prevSearchRef.current = debouncedSearchTerm;
      setCurrentPage(1);
      fetchUsers(1, itemsPerPage, debouncedSearchTerm);
    } else {
      fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, fetchUsers]);

  // Load activity logs when opening Activity Monitor tab (and when searching inside it)
  useEffect(() => {
    if (activeTab !== 'activity-monitor') return;
    fetchActivityLogs(searchTerm);
  }, [activeTab, searchTerm, fetchActivityLogs]);

  useEffect(() => {
    if (activeTab !== 'security') return;
    fetchSecurityLogs(searchTerm);
  }, [activeTab, searchTerm, fetchSecurityLogs]);

  useEffect(() => {
    if (activeTab !== 'communication') return;
    fetchCommunicationLogs(searchTerm);
  }, [activeTab, searchTerm, fetchCommunicationLogs]);

  // Current page users from API (no client-side slice)
  const displayUsers = users;
  // Server-side total and pages
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const paginatedUsers = displayUsers;

  // Bulk selection hook - MUST be after paginatedUsers is calculated
  const {
    selectedItems,
    selectedCount,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    isAllSelected,
    isSomeSelected,
    getSelectedItemsData,
    deselectAll
  } = useBulkSelection(paginatedUsers, 'id');

  // Clear selection when search term changes
  useEffect(() => {
    if (selectedCount > 0) deselectAll();
  }, [debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 1 when items per page changes
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    deselectAll(); // Clear selection when page size changes
  };

  // Bulk operations - Defined AFTER hook to access hook functions
  const handleBulkDelete = async () => {
    const selectedUsers = getSelectedItemsData(paginatedUsers);
    if (selectedUsers.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedUsers.length} user(s)?\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const deletePromises = selectedUsers.map(user => 
        fetch(`${API_BASE_URL}/api/admin/users/${user.id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        })
      );

      const results = await Promise.allSettled(deletePromises);
      const failed = results.filter(r => r.status === 'rejected' || !r.value.ok);
      
      if (failed.length > 0) {
        throw new Error(`${failed.length} user(s) failed to delete`);
      }

      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      deselectAll();
      alert(`Successfully deleted ${selectedUsers.length} user(s)!`);
    } catch (err) {
      console.error('Error in bulk delete:', err);
      setError(err.message || 'Failed to delete some users');
      alert(`Error: ${err.message || 'Failed to delete some users'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    const selectedUsers = getSelectedItemsData(paginatedUsers);
    if (selectedUsers.length === 0) return;

    try {
      setLoading(true);
      setError(null);
      
      const activatePromises = selectedUsers.map(user => 
        fetch(`${API_BASE_URL}/api/admin/users/${user.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ is_active: true })
        })
      );

      const results = await Promise.allSettled(activatePromises);
      const failed = results.filter(r => r.status === 'rejected' || !r.value.ok);
      
      if (failed.length > 0) {
        throw new Error(`${failed.length} user(s) failed to activate`);
      }

      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      deselectAll();
      alert(`Successfully activated ${selectedUsers.length} user(s)!`);
    } catch (err) {
      console.error('Error in bulk activate:', err);
      setError(err.message || 'Failed to activate some users');
      alert(`Error: ${err.message || 'Failed to activate some users'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    const selectedUsers = getSelectedItemsData(paginatedUsers);
    if (selectedUsers.length === 0) return;

    const confirmMessage = `Are you sure you want to deactivate ${selectedUsers.length} user(s)?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const deactivatePromises = selectedUsers.map(user => 
        fetch(`${API_BASE_URL}/api/admin/users/${user.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ is_active: false })
        })
      );

      const results = await Promise.allSettled(deactivatePromises);
      const failed = results.filter(r => r.status === 'rejected' || !r.value.ok);
      
      if (failed.length > 0) {
        throw new Error(`${failed.length} user(s) failed to deactivate`);
      }

      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      deselectAll();
      alert(`Successfully deactivated ${selectedUsers.length} user(s)!`);
    } catch (err) {
      console.error('Error in bulk deactivate:', err);
      setError(err.message || 'Failed to deactivate some users');
      alert(`Error: ${err.message || 'Failed to deactivate some users'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkExport = () => {
    const selectedUsers = getSelectedItemsData(paginatedUsers);
    if (selectedUsers.length === 0) return;

    // Prepare data for export
    const exportData = selectedUsers.map(user => ({
      ID: user.id,
      'First Name': user.first_name || '',
      'Last Name': user.last_name || '',
      Name: user.name,
      Email: user.email,
      Role: user.role,
      Status: user.status,
      'Join Date': user.joinDate,
      Country: user.country,
      Phone: user.phone
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `users_export_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
    alert(`Exported ${selectedUsers.length} user(s) to ${filename}`);
  };

  const handleAddUser = () => {
    if (formData.first_name && formData.last_name && formData.email) {
      createUser(formData);
    } else {
      alert('Please fill in all required fields (First Name, Last Name, Email)');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email,
      phone: user.phone || '',
      country: user.country || '',
      city: '',
      role: user.role || 'student',
      status: user.status
    });
  };

  const handleUpdateUser = () => {
    if (editingUser && formData.first_name && formData.last_name && formData.email) {
      updateUser(editingUser.id, formData);
    } else {
      alert('Please fill in all required fields (First Name, Last Name, Email)');
    }
  };

  const handleDeleteUser = (userId) => {
    deleteUser(userId);
  };

  const handleStatusToggle = (userId, currentStatus) => {
    toggleUserStatus(userId, currentStatus);
  };

  // Profile management functions
  const handleViewProfile = (user) => {
    // Show user profile details in a modal or navigate to profile page
    alert(`Viewing profile for ${user.name} (${user.email})\n\nProfile Details:\n- ID: ${user.id}\n- Role: ${user.role}\n- Status: ${user.status}\n- Join Date: ${user.joinDate}\n- Country: ${user.country || 'N/A'}\n- Phone: ${user.phone || 'N/A'}`);
  };

  const handleEditProfile = (user) => {
    // Open edit profile form
    setEditingUser(user);
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email,
      phone: user.phone || '',
      country: user.country || '',
      city: '',
      role: user.role || 'student',
      status: user.status
    });
  };

  const handleDeleteProfile = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user profile? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user profile');
      }

      console.log('User profile deleted successfully');
      
      // Refresh user list
      await fetchUsers(currentPage, itemsPerPage, debouncedSearchTerm);
      
      alert('User profile deleted successfully!');
    } catch (err) {
      console.error('Error deleting user profile:', err);
      setError(err.message);
      alert(`Failed to delete user profile: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'user-list', label: t('userManagement.tabs.userList'), icon: 'people' },
    { id: 'user-profiles', label: t('userManagement.tabs.userProfiles'), icon: 'person' },
    { id: 'search-filters', label: t('userManagement.tabs.searchFilters'), icon: 'search' },
    { id: 'roles-permissions', label: t('userManagement.tabs.rolesPermissions'), icon: 'admin_panel_settings' },
    { id: 'communication', label: t('userManagement.tabs.communication'), icon: 'message' },
    { id: 'bulk-operations', label: t('userManagement.tabs.bulkOperations'), icon: 'batch_prediction' },
    { id: 'activity-monitor', label: t('userManagement.tabs.activityMonitor'), icon: 'monitor' },
    { id: 'security', label: t('userManagement.tabs.security'), icon: 'security' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'user-list':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('userManagement.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={() => setShowAddForm(true)}
              >
                <span className="material-icons">person_add</span>
                {t('userManagement.addUser')}
              </button>
            </div>

            {loading && (
              <div className="loading-message">
                <span className="material-icons rotating">refresh</span>
                {t('userManagement.loadingUsers')}
              </div>
            )}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Bulk Actions Toolbar */}
            <BulkActions
              selectedCount={selectedCount}
              onDelete={handleBulkDelete}
              onActivate={handleBulkActivate}
              onDeactivate={handleBulkDeactivate}
              onExport={handleBulkExport}
              onClear={deselectAll}
              loading={loading}
            />

            <table className="users-table">
                <thead>
                  <tr>
                    <th className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={isAllSelected(paginatedUsers)}
                        ref={(input) => {
                          if (input) input.indeterminate = isSomeSelected(paginatedUsers);
                        }}
                        onChange={() => toggleSelectAll(paginatedUsers)}
                        title={isAllSelected(paginatedUsers) ? t('userManagement.actions.deselectAll') : t('userManagement.actions.selectAll')}
                      />
                    </th>
                    <th>{t('userManagement.tableHeaders.id')}</th>
                    <th>{t('userManagement.tableHeaders.name')}</th>
                    <th>{t('userManagement.tableHeaders.email')}</th>
                    <th>{t('userManagement.tableHeaders.role')}</th>
                    <th>{t('userManagement.tableHeaders.status')}</th>
                    <th>{t('userManagement.tableHeaders.joinDate')}</th>
                    <th>{t('userManagement.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-users-message">
                        <div className="empty-state">
                          <span className="material-icons">people_outline</span>
                          <p>{t('userManagement.noUsersFound')}</p>
                          <small>{t('userManagement.tryAdding')}</small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map(user => (
                    <tr key={user.id} className={isSelected(user.id) ? 'row-selected' : ''}>
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={isSelected(user.id)}
                          onChange={() => toggleSelection(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {translateRole(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.status}`}>
                          {translateStatus(user.status)}
                        </span>
                      </td>
                      <td>{user.joinDate}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit"
                            onClick={() => handleEditUser(user)}
                            title={t('userManagement.actions.editUser')}
                          >
                            <span className="material-icons">edit</span>
                          </button>
                          <button 
                            className="action-btn toggle"
                              onClick={() => handleStatusToggle(user.id, user.status)}
                            title={user.status === 'active' ? t('userManagement.actions.deactivate') : t('userManagement.actions.activate')}
                          >
                            <span className="material-icons">
                              {user.status === 'active' ? 'block' : 'check_circle'}
                            </span>
                          </button>
                          <button 
                            className="action-btn delete"
                            onClick={() => handleDeleteUser(user.id)}
                            title={t('userManagement.actions.deleteUser')}
                          >
                            <span className="material-icons">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination - User List */}
              {(totalCount > 0 || displayUsers.length > 0) && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  showItemsPerPage={true}
                />
              )}
          </div>
        );

      case 'user-profiles':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search profiles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button 
                className="add-user-btn"
                onClick={() => setShowAddForm(true)}
              >
                <span className="material-icons">person_add</span>
                Add Profile
              </button>
            </div>

            {loading && (
              <div className="loading-message">
                <span className="material-icons rotating">refresh</span>
                Loading profiles...
              </div>
            )}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Bulk Actions Toolbar */}
            <BulkActions
              selectedCount={selectedCount}
              onDelete={handleBulkDelete}
              onActivate={handleBulkActivate}
              onDeactivate={handleBulkDeactivate}
              onExport={handleBulkExport}
              onClear={deselectAll}
              loading={loading}
            />

            <table className="users-table">
                <thead>
                  <tr>
                    <th className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={isAllSelected(paginatedUsers)}
                        ref={(input) => {
                          if (input) input.indeterminate = isSomeSelected(paginatedUsers);
                        }}
                        onChange={() => toggleSelectAll(paginatedUsers)}
                        title={isAllSelected(paginatedUsers) ? t('userManagement.actions.deselectAll') : t('userManagement.actions.selectAll')}
                      />
                    </th>
                    <th>{t('userManagement.tableHeaders.id')}</th>
                    <th>{t('userManagement.tableHeaders.name')}</th>
                    <th>{t('userManagement.tableHeaders.email')}</th>
                    <th>{t('userManagement.tableHeaders.role')}</th>
                    <th>{t('userManagement.tableHeaders.status')}</th>
                    <th>{t('userManagement.tableHeaders.lastLogin')}</th>
                    <th>{t('userManagement.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-users-message">
                        <div className="empty-state">
                          <span className="material-icons">person_outline</span>
                          <p>{t('userManagement.noUsersFound')}</p>
                          <small>{t('userManagement.tryAdding')}</small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedUsers.map(user => (
                    <tr key={user.id} className={isSelected(user.id) ? 'row-selected' : ''}>
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={isSelected(user.id)}
                          onChange={() => toggleSelection(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {translateRole(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.status}`}>
                          {translateStatus(user.status)}
                        </span>
                      </td>
                        <td>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</td>
                      <td>
                        <div className="action-buttons">
                            <button 
                              className="action-btn edit" 
                              onClick={() => handleViewProfile(user)}
                              title={t('userManagement.actions.editUser')}
                            >
                            <span className="material-icons">visibility</span>
                          </button>
                            <button 
                              className="action-btn toggle" 
                              onClick={() => handleEditProfile(user)}
                              title={t('userManagement.actions.editUser')}
                            >
                            <span className="material-icons">edit</span>
                          </button>
                            <button 
                              className="action-btn delete" 
                              onClick={() => handleDeleteProfile(user.id)}
                              title={t('userManagement.actions.deleteUser')}
                            >
                            <span className="material-icons">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
              
              {/* Pagination */}
              {(totalCount > 0 || displayUsers.length > 0) && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  showItemsPerPage={true}
                />
              )}
          </div>
        );

      case 'search-filters':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Advanced search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button className="add-user-btn">
                <span className="material-icons">filter_list</span>
                Apply Filters
              </button>
            </div>

            {/* Bulk Actions Toolbar */}
            <BulkActions
              selectedCount={selectedCount}
              onDelete={handleBulkDelete}
              onActivate={handleBulkActivate}
              onDeactivate={handleBulkDeactivate}
              onExport={handleBulkExport}
              onClear={deselectAll}
              loading={loading}
            />

            <table className="users-table">
                <thead>
                  <tr>
                    <th className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={isAllSelected(paginatedUsers)}
                        ref={(input) => {
                          if (input) input.indeterminate = isSomeSelected(paginatedUsers);
                        }}
                        onChange={() => toggleSelectAll(paginatedUsers)}
                        title={isAllSelected(paginatedUsers) ? t('userManagement.actions.deselectAll') : t('userManagement.actions.selectAll')}
                      />
                    </th>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Country</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map(user => (
                    <tr key={user.id} className={isSelected(user.id) ? 'row-selected' : ''}>
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={isSelected(user.id)}
                          onChange={() => toggleSelection(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {translateRole(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.status}`}>
                          {translateStatus(user.status)}
                        </span>
                      </td>
                      <td>{user.country}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn edit" title="View Details">
                            <span className="material-icons">visibility</span>
                          </button>
                          <button className="action-btn toggle" title="Export">
                            <span className="material-icons">download</span>
                          </button>
                          <button className="action-btn delete" title="Remove">
                            <span className="material-icons">remove_circle</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination */}
              {(totalCount > 0 || displayUsers.length > 0) && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  showItemsPerPage={true}
                />
              )}
          </div>
        );

      case 'roles-permissions':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button className="add-user-btn">
                <span className="material-icons">add_circle</span>
                Add Role
              </button>
            </div>

            <table className="users-table">
                <thead>
                  <tr>
                    <th>{t('userManagement.tableHeaders.id')}</th>
                    <th>{t('userManagement.tableHeaders.roleName')}</th>
                    <th>{t('userManagement.tableHeaders.description')}</th>
                    <th>{t('userManagement.tableHeaders.users')}</th>
                    <th>{t('userManagement.tableHeaders.status')}</th>
                    <th>{t('userManagement.tableHeaders.created')}</th>
                    <th>{t('userManagement.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>Admin</td>
                    <td>Full system access</td>
                    <td>3</td>
                    <td>
                      <span className="status-badge active">{t('userManagement.forms.active')}</span>
                    </td>
                    <td>2024-01-01</td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" title={t('userManagement.actions.edit')}>
                          <span className="material-icons">edit</span>
                        </button>
                        <button className="action-btn toggle" title={t('userManagement.tableHeaders.permissions')}>
                          <span className="material-icons">security</span>
                        </button>
                        <button className="action-btn delete" title={t('userManagement.actions.delete')}>
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>User</td>
                    <td>Standard user access</td>
                    <td>1244</td>
                    <td>
                      <span className="status-badge active">{t('userManagement.forms.active')}</span>
                    </td>
                    <td>2024-01-01</td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" title={t('userManagement.actions.edit')}>
                          <span className="material-icons">edit</span>
                        </button>
                        <button className="action-btn toggle" title={t('userManagement.tableHeaders.permissions')}>
                          <span className="material-icons">security</span>
                        </button>
                        <button className="action-btn delete" title={t('userManagement.actions.delete')}>
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
          </div>
        );

      case 'communication':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button className="add-user-btn" type="button" onClick={() => fetchCommunicationLogs(searchTerm)} disabled={loadingCommunication}>
                <span className="material-icons">send</span>
                {loadingCommunication ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Recipient</th>
                    <th>Subject</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Sent Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {communicationLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-users-message">
                        <div className="empty-state">
                          <span className="material-icons">message</span>
                          <p>No messages yet.</p>
                          <small>Communication history is taken from Audit Logs (e.g. bulk email).</small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    communicationLogs.map((log) => {
                      const after = log.after_state || {};
                      const subject = after?.subject || (log.description || '').split('\n')[0] || '—';
                      const recipients = after?.recipients ?? null;
                      const recipientLabel =
                        typeof recipients === 'number'
                          ? `${recipients} recipients`
                          : formatAuditActorLabel(log);
                      const status = (log.status || 'success').toString();
                      return (
                        <tr key={log.id}>
                          <td>{log.id}</td>
                          <td>{recipientLabel}</td>
                          <td>{subject}</td>
                          <td><span className="role-badge user">Email</span></td>
                          <td>
                            <span className={`status-badge ${status === 'success' ? 'active' : 'inactive'}`}>
                              {status}
                            </span>
                          </td>
                          <td title={log.created_at}>{new Date(log.created_at).toISOString().slice(0, 10)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn edit"
                                title="View Details"
                                type="button"
                                onClick={() => alert(`Action: ${log.action}\nTime: ${log.created_at}\n\n${log.description || ''}`)}
                              >
                                <span className="material-icons">visibility</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
          </div>
        );

      case 'bulk-operations':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search operations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button className="add-user-btn" type="button" onClick={() => setShowMassEmailModal(true)}>
                <span className="material-icons">playlist_add</span>
                New Operation
              </button>
            </div>

            {showMassEmailModal && (
              <div className="modal-overlay" onClick={() => setShowMassEmailModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
                  <div className="modal-header">
                    <h3>Mass Email</h3>
                    <button className="close-btn" onClick={() => setShowMassEmailModal(false)} aria-label="Close">×</button>
                  </div>

                  <div className="modal-body">
                    <p style={{ marginTop: 0, color: '#666' }}>
                      This will email users matching your current search filter.
                      {debouncedSearchTerm ? ` Filter: "${debouncedSearchTerm}"` : ' Filter: (none)'}
                    </p>
                    <div className="form-group">
                      <label>Subject</label>
                      <input
                        className="form-input"
                        value={massEmailSubject}
                        onChange={(e) => setMassEmailSubject(e.target.value)}
                        placeholder="Subject..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea
                        className="form-input"
                        rows={8}
                        value={massEmailMessage}
                        onChange={(e) => setMassEmailMessage(e.target.value)}
                        placeholder="Write your message..."
                      />
                    </div>
                  </div>

                  <div className="modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button className="secondary-btn" onClick={() => setShowMassEmailModal(false)} type="button">
                      Cancel
                    </button>
                    <button className="add-user-btn" onClick={sendMassEmail} disabled={sendingMassEmail} type="button">
                      {sendingMassEmail ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Operation</th>
                    <th>Target Users</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lastBulkEmailResult ? (
                    <tr>
                      <td>{lastBulkEmailResult.id}</td>
                      <td>{lastBulkEmailResult.operation}</td>
                      <td>{lastBulkEmailResult.target}</td>
                      <td><span className="role-badge user">{lastBulkEmailResult.type}</span></td>
                      <td><span className="status-badge active">{lastBulkEmailResult.status}</span></td>
                      <td>{lastBulkEmailResult.created}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn edit"
                            title={`Sent: ${lastBulkEmailResult.sent}, Failed: ${lastBulkEmailResult.failed}`}
                            onClick={() => alert(`Sent: ${lastBulkEmailResult.sent}\nFailed: ${lastBulkEmailResult.failed}`)}
                          >
                            <span className="material-icons">info</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-users-message">
                        <div className="empty-state">
                          <span className="material-icons">playlist_add</span>
                          <p>No operations yet.</p>
                          <small>Click “New Operation” to send a mass email.</small>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
          </div>
        );

      case 'activity-monitor':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button className="add-user-btn" type="button" onClick={() => fetchActivityLogs(searchTerm)} disabled={loadingActivity}>
                <span className="material-icons">refresh</span>
                {loadingActivity ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Activity</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Timestamp</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-users-message">
                        <div className="empty-state">
                          <span className="material-icons">monitor</span>
                          <p>No activity yet.</p>
                          <small>Activity is taken from Audit Logs.</small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    activityLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.id}</td>
                        <td>{formatAuditActorLabel(log)}</td>
                        <td>{log.description || log.action}</td>
                        <td>
                          <span className="role-badge user">{(log.resource_type || 'system').toString()}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${log.status === 'success' ? 'active' : 'inactive'}`}>
                            {(log.status || 'success').toString()}
                          </span>
                        </td>
                        <td title={log.created_at}>{formatTimeAgo(log.created_at)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit"
                              title="View Details"
                              type="button"
                              onClick={() => alert(`Action: ${log.action}\nUser: ${formatAuditActorLabel(log)}\nTime: ${log.created_at}\n\n${log.description || ''}`)}
                            >
                              <span className="material-icons">visibility</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
          </div>
        );

      case 'security':
        return (
          <div className="tab-content">
            <div className="user-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search security events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <button className="add-user-btn" type="button" onClick={() => fetchSecurityLogs(searchTerm)} disabled={loadingSecurity}>
                <span className="material-icons">security</span>
                {loadingSecurity ? 'Scanning...' : 'Security Scan'}
              </button>
            </div>

            <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Event</th>
                    <th>Description</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Detected</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {securityLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-users-message">
                        <div className="empty-state">
                          <span className="material-icons">security</span>
                          <p>No security events yet.</p>
                          <small>Security events are derived from Audit Logs.</small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    securityLogs.map((log) => {
                      const severity = guessSeverity(log);
                      const status = (log.status || 'success').toString();
                      return (
                        <tr key={log.id}>
                          <td>{log.id}</td>
                          <td>{guessSecurityEvent(log)}</td>
                          <td>{log.description || log.action}</td>
                          <td><span className="role-badge user">{severity}</span></td>
                          <td>
                            <span className={`status-badge ${status === 'success' ? 'active' : 'inactive'}`}>
                              {status}
                            </span>
                          </td>
                          <td title={log.created_at}>{formatTimeAgo(log.created_at)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn edit"
                                title="View Details"
                                type="button"
                                onClick={() => alert(`Action: ${log.action}\nUser: ${formatAuditActorLabel(log)}\nIP: ${log.ip_address || ''}\nTime: ${log.created_at}\n\n${log.description || ''}`)}
                              >
                                <span className="material-icons">visibility</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
          </div>
        );

      default:
        return <div>Select a tab</div>;
    }
  };

  if (loading) {
    return (
      <div className="section">
        <h2>User Management</h2>
        <div className="loading">{t('userManagement.loadingUsers')}</div>
      </div>
    );
  }

  return (
    <div className="section user-management-section">
      <h2>User Management</h2>
      
      {/* Horizontal Tabs */}
      <div className="user-management-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-icons">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Add User Form */}
      {showAddForm && (
        <div className="user-form-overlay">
          <div className="user-form">
            <h3>{t('userManagement.forms.addNewUser')}</h3>
            {error && <div className="error-message">{error}</div>}
            <input
              type="text"
              placeholder={t('userManagement.forms.firstName')}
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              required
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.lastName')}
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              required
            />
            <input
              type="email"
              placeholder={t('userManagement.forms.email')}
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.phone')}
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.country')}
              value={formData.country}
              onChange={(e) => setFormData({...formData, country: e.target.value})}
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.city')}
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
            />
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
            >
              <option value="student">{t('userManagement.forms.student')}</option>
              <option value="parent">{t('userManagement.forms.parent')}</option>
              <option value="teacher">{t('userManagement.forms.teacher')}</option>
              <option value="agent">{t('userManagement.forms.agent')}</option>
            </select>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
            >
              <option value="active">{t('userManagement.forms.active')}</option>
              <option value="inactive">{t('userManagement.forms.inactive')}</option>
            </select>
            <div className="form-buttons">
              <button onClick={handleAddUser} disabled={loading}>
                {loading ? t('userManagement.adding') : t('userManagement.addUser')}
              </button>
              <button onClick={() => setShowAddForm(false)} disabled={loading}>{t('userManagement.actions.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Form */}
      {editingUser && (
        <div className="user-form-overlay">
          <div className="user-form">
            <h3>{t('userManagement.forms.editUser')}</h3>
            {error && <div className="error-message">{error}</div>}
            <input
              type="text"
              placeholder={t('userManagement.forms.firstName')}
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              required
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.lastName')}
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              required
            />
            <input
              type="email"
              placeholder={t('userManagement.forms.email')}
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.phone')}
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.country')}
              value={formData.country}
              onChange={(e) => setFormData({...formData, country: e.target.value})}
            />
            <input
              type="text"
              placeholder={t('userManagement.forms.city')}
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
            />
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
            >
              <option value="student">{t('userManagement.forms.student')}</option>
              <option value="parent">{t('userManagement.forms.parent')}</option>
              <option value="teacher">{t('userManagement.forms.teacher')}</option>
              <option value="agent">{t('userManagement.forms.agent')}</option>
            </select>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
            >
              <option value="active">{t('userManagement.forms.active')}</option>
              <option value="inactive">{t('userManagement.forms.inactive')}</option>
            </select>
            <div className="form-buttons">
              <button onClick={handleUpdateUser} disabled={loading}>
                {loading ? 'Updating...' : 'Update User'}
              </button>
              <button onClick={() => {
                setEditingUser(null);
                setFormData({ first_name: '', last_name: '', email: '', phone: '', country: '', city: '', role: 'student', status: 'active' });
              }} disabled={loading}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 